import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Category, Product } from '../types';

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<Category[]>('/categories')).data,
  });
}

export function useCategory(slug: string) {
  return useQuery({
    queryKey: ['categories', slug],
    queryFn: async () => (await api.get<Category>(`/categories/${slug}`)).data,
    enabled: !!slug,
  });
}

export function useProducts(categorySlug?: string, sort?: string) {
  return useQuery({
    queryKey: ['products', categorySlug ?? 'all', sort ?? ''],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (categorySlug) params.category = categorySlug;
      if (sort) params.sort = sort;
      return (await api.get<Product[]>('/products', { params })).data;
    },
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ['products', slug],
    queryFn: async () => (await api.get<Product>(`/products/${slug}`)).data,
    enabled: !!slug,
  });
}

export interface ShopProductsParams {
  search?: string;
  category?: string;
  category_id?: number;
  min_price?: number;
  max_price?: number;
  sort?: string;
  page?: number;
  per_page?: number;
}

export interface ShopProductsResult {
  data: Product[];
  meta: {
    total: number;
    current_page: number;
    last_page: number;
    per_page: number;
  };
}

export function useShopProducts(params: ShopProductsParams = {}) {
  const queryParams: Record<string, string | number> = {};
  if (params.search)      queryParams.search      = params.search;
  if (params.category)    queryParams.category    = params.category;
  if (params.category_id) queryParams.category_id = params.category_id;
  if (params.min_price != null) queryParams.min_price = params.min_price;
  if (params.max_price != null) queryParams.max_price = params.max_price;
  if (params.sort)        queryParams.sort        = params.sort;
  if (params.page)        queryParams.page        = params.page;
  if (params.per_page)    queryParams.per_page    = params.per_page;

  return useQuery({
    queryKey: ['shop-products', queryParams],
    queryFn: async () => {
      const res = await api.get<ShopProductsResult>('/shop', { params: queryParams });
      return res.data;
    },
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
}

export interface SectionProductConfig {
  source?: string;
  limit?: number;
  category_id?: number;
  product_ids?: number[];
  sort?: string;
}

export function useInfiniteShopProducts(params: Omit<ShopProductsParams, 'page'>) {
  const queryParams: Record<string, string | number> = {};
  if (params.search)            queryParams.search      = params.search;
  if (params.category)          queryParams.category    = params.category;
  if (params.category_id)       queryParams.category_id = params.category_id;
  if (params.min_price != null) queryParams.min_price   = params.min_price;
  if (params.max_price != null) queryParams.max_price   = params.max_price;
  if (params.sort)              queryParams.sort        = params.sort;
  if (params.per_page)          queryParams.per_page    = params.per_page;

  return useInfiniteQuery({
    queryKey: ['shop-products-infinite', queryParams],
    queryFn: async ({ pageParam }) => {
      const res = await api.get<ShopProductsResult>('/shop', {
        params: { ...queryParams, page: pageParam },
      });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
    staleTime: 30 * 1000,
  });
}

export function useSectionProducts(config: SectionProductConfig) {
  const params: Record<string, string | number> = {};
  if (config.source) params.source = config.source;
  if (config.limit) params.limit = config.limit;
  if (config.category_id) params.category_id = config.category_id;
  if (config.product_ids?.length) params.ids = config.product_ids.join(',');
  if (config.sort) params.sort = config.sort;

  return useQuery({
    queryKey: ['section-products', params],
    queryFn: async () => (await api.get<Product[]>('/products', { params })).data,
    staleTime: 2 * 60 * 1000,
  });
}
